import type { ActiveFilter } from '../../ideas/filters/FiltersMenu';
import type { SessionFilter } from '../../../api/discoveryAgent';

export const PRODUCT_AREA_PARENT = 'product-area';
export const SOURCE_PARENT = 'evidence-source';

/** MongoDB ObjectId as 24 hex chars (string). */
const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

export function isMongoObjectIdString(id: string): boolean {
  return OBJECT_ID_RE.test(String(id).trim());
}

/** Human-readable label for a filter option (handles nested `label: { label: "..." }` from API). */
export function resolveOptionDisplayLabel(o: any): string {
  if (!o || typeof o !== 'object') return '';
  const nested =
    o.label != null &&
    typeof o.label === 'object' &&
    !Array.isArray(o.label) &&
    (o.label as { label?: string }).label != null
      ? String((o.label as { label?: string }).label).trim()
      : '';
  if (nested) return nested;
  if (typeof o.label === 'string' && o.label.trim()) return o.label.trim();
  if (o.name != null && String(o.name).trim()) return String(o.name).trim();
  if (o.title != null && String(o.title).trim()) return String(o.title).trim();
  return '';
}

function registerOptionLookupKeys(o: any, display: string, lookup: Map<string, string>): void {
  const add = (raw: unknown) => {
    if (raw == null || raw === '') return;
    const s = String(raw).trim();
    if (!s) return;
    lookup.set(s, display);
    lookup.set(s.toLowerCase(), display);
  };
  add(o._id);
  add(o.value);
  if (typeof o.label === 'string' && o.label.trim()) add(o.label.trim());
  add(o.domain);
}

function visitOptionForSelectedMap(o: any, lookup: Map<string, string>): void {
  if (!o || typeof o !== 'object') return;
  let display = resolveOptionDisplayLabel(o);
  if (!display) {
    if (o.value != null && String(o.value).trim()) display = String(o.value).trim();
    else if (o._id != null) display = String(o._id);
    else return;
  }
  registerOptionLookupKeys(o, display, lookup);
  if (Array.isArray(o.childComponents)) {
    o.childComponents.forEach((c: any) => visitOptionForSelectedMap(c, lookup));
  }
}

/**
 * Maps each selected value (slug, ObjectId, etc.) → display name by walking the filter options tree.
 */
export function buildSelectedFilterMapFromOptions(
  selected: string[],
  options: unknown
): Record<string, string> | undefined {
  const lookup = new Map<string, string>();
  if (Array.isArray(options)) {
    options.forEach((o) => visitOptionForSelectedMap(o, lookup));
  } else if (options && typeof options === 'object') {
    Object.values(options as object).forEach((o) => visitOptionForSelectedMap(o, lookup));
  }
  const out: Record<string, string> = {};
  for (const raw of selected) {
    const id = String(raw);
    const label = lookup.get(id) ?? lookup.get(id.toLowerCase());
    if (label != null && label !== '') {
      out[id] = label;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function collectObjectIdLabelLookup(options: unknown, lookup: Map<string, string>): void {
  const addPair = (rawId: unknown, rawLabel: unknown) => {
    if (rawId == null) return;
    const id = String(rawId);
    if (!isMongoObjectIdString(id)) return;
    const label =
      rawLabel != null && String(rawLabel).trim() !== ''
        ? String(rawLabel)
        : id;
    lookup.set(id.toLowerCase(), label);
    lookup.set(id, label);
  };

  const visit = (o: any) => {
    if (!o || typeof o !== 'object') return;
    const id = o._id ?? o.value;
    const lab =
      resolveOptionDisplayLabel(o) ||
      (o.name != null && String(o.name).trim() !== '' ? String(o.name) : '') ||
      (typeof o.label === 'string' && o.label.trim() ? o.label.trim() : '') ||
      (o.title != null && String(o.title).trim() !== '' ? String(o.title) : '') ||
      id;
    addPair(id, lab);
    if (Array.isArray(o.childComponents)) {
      o.childComponents.forEach(visit);
    }
  };

  if (Array.isArray(options)) {
    options.forEach(visit);
  } else if (options && typeof options === 'object') {
    Object.values(options as object).forEach(visit);
  }
}

/**
 * For selected values that are ObjectIds, builds id → label using static filter options (checkbox trees).
 */
export function buildSelectedFilterMapForObjectIdsFromOptions(
  selected: string[],
  options: unknown
): Record<string, string> | undefined {
  const lookup = new Map<string, string>();
  collectObjectIdLabelLookup(options, lookup);
  const out: Record<string, string> = {};
  for (const raw of selected) {
    const id = String(raw);
    if (!isMongoObjectIdString(id)) continue;
    out[id] = lookup.get(id) ?? lookup.get(id.toLowerCase()) ?? id;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Looks up static `options` for a filter `parent` in ideas filter config (idea / evidence / account children).
 */
export function getFilterOptionsForParent(parent: string, ideasFiltersConfig: unknown): unknown {
  if (!ideasFiltersConfig || typeof ideasFiltersConfig !== 'object') return undefined;
  for (const route of ['idea', 'evidence'] as const) {
    const section = (ideasFiltersConfig as Record<string, unknown>)[route];
    if (!section || typeof section !== 'object') continue;
    const sec = section as Record<string, { options?: unknown; childFilters?: Record<string, unknown> }>;
    const direct = sec[parent];
    if (direct?.options != null) return direct.options;
    const account = sec.account?.childFilters;
    if (account && typeof account === 'object') {
      for (const key of Object.keys(account)) {
        if (key === '_id') continue;
        const child = account[key] as { parent?: string; options?: unknown };
        if (child?.parent === parent && child.options != null) return child.options;
      }
    }
  }
  return undefined;
}

/**
 * For each selected value that is a Mongo ObjectId, sets `selectedFilterMap[id]` to the option display name
 * when `options` is available. Preserves non–ObjectId entries already on the filter.
 */
export function mergeObjectIdSelectedFilterMapFromOptions(
  f: SessionFilter,
  options: unknown | undefined
): SessionFilter {
  const selected = (f.selected ?? []).map(String);
  if (selected.length === 0 || options == null) return f;
  if (!selected.some((id) => isMongoObjectIdString(id))) return f;
  const fromOpts = buildSelectedFilterMapForObjectIdsFromOptions(selected, options);
  if (!fromOpts || Object.keys(fromOpts).length === 0) return f;
  const selSet = new Set(selected);
  const prev = f.selectedFilterMap ?? {};
  const pruned = Object.fromEntries(Object.entries(prev).filter(([k]) => selSet.has(k)));
  return {
    ...f,
    selectedFilterMap: { ...pruned, ...fromOpts },
  };
}

/**
 * Applies {@link mergeObjectIdSelectedFilterMapFromOptions} for every filter using config-derived options.
 */
export function enrichSessionFiltersObjectIdMaps(
  filters: SessionFilter[],
  ideasFiltersConfig: unknown
): SessionFilter[] {
  if (!Array.isArray(filters) || filters.length === 0) return filters;
  return filters.map((f) =>
    mergeObjectIdSelectedFilterMapFromOptions(f, getFilterOptionsForParent(f.parent, ideasFiltersConfig))
  );
}

/**
 * Ensures `selectedFilterMap` includes every ObjectId in `selected`, using parallel `selectedLabels`
 * and any existing map entries.
 */
export function enrichSessionFilterSelectedFilterMap(f: SessionFilter): SessionFilter {
  const selected = (f.selected ?? []).map(String);
  if (selected.length === 0) return f;

  const labels = f.selectedLabels?.map(String);
  const prev = f.selectedFilterMap;
  const additions: Record<string, string> = {};

  selected.forEach((id, i) => {
    const existing = prev?.[id];
    if (existing != null && String(existing).trim() !== '') return;
    const label = labels?.[i];
    if (label != null && String(label).trim() !== '') {
      additions[id] = String(label);
    }
  });

  if (Object.keys(additions).length === 0) return f;

  return {
    ...f,
    selectedFilterMap: { ...(prev ?? {}), ...additions },
  };
}

/**
 * Returns 'productArea' | 'source' | 'customers' based on the filter's parent key.
 * No placement field is persisted — restoration always derives type from parent.
 */
export function getFilterTypeFromParent(parent: string): 'productArea' | 'source' | 'customers' {
  if (parent === PRODUCT_AREA_PARENT) return 'productArea';
  if (parent === SOURCE_PARENT) return 'source';
  return 'customers';
}

/**
 * Builds SessionFilter[] from store or context filters.
 * Stamps filterObject: 'evidence' on every filter so FiltersManager routes to the evidence collection.
 */
export function buildSessionFilters(
  filters: ActiveFilter[] | SessionFilter[],
  _customerParentKeys?: Set<string>
): SessionFilter[] {
  return filters.map((f) => {
    const sessionF = f as SessionFilter;
    const enriched = enrichSessionFilterSelectedFilterMap(sessionF);
    const stripLabels = enriched.selectedLabels !== undefined;
    if (enriched === sessionF && !stripLabels && sessionF.filterObject === 'evidence') {
      return sessionF;
    }
    const { selectedLabels: _displayOnly, ...rest } = enriched;
    return { ...rest, filterObject: 'evidence' as const };
  });
}

/**
 * Derives customer evidence parent keys from ideasFiltersConfig (plan, region, nps, etc.).
 */
export function getCustomerParentKeysFromConfig(ideasFiltersConfig: {
  evidence?: { account?: { childFilters?: Record<string, { parent?: string }> } };
}): Set<string> {
  const account = ideasFiltersConfig?.evidence?.account;
  const childFilters = account?.childFilters ?? {};
  const keys = Object.keys(childFilters).filter((name) => name !== '_id');
  return new Set(
    keys.map((name) => childFilters[name]?.parent).filter((p): p is string => Boolean(p))
  );
}
